// `/chrome-extension-api/ats-filters/*` — the tenant's ATS-filter configuration, which decides
// whether the criteria builder's eighth section exists at all and what its Field select offers.
//
// ⚠️ BOTH CALLS ARE GETs WITH QUERY PARAMS, and the second sends an ARRAY. Upstream passes
// `sections` as a repeated `sections=` parameter (Angular's default array serialisation), not
// as a comma-joined string — which is the opposite of `getJobs`'s `exclude[]`, where the join
// is what the server wants. The two are a matched pair of easy mistakes.
import { request } from './request.ts'

/** `AtsFiltersFieldType`. String values, on the wire. */
export const AtsFiltersFieldType = {
  Text: 'TEXT',
  Numeric: 'NUMERIC',
  Date: 'DATE',
  MultiSelect: 'MULTI_SELECT',
  SingleSelect: 'SINGLE_SELECT',
} as const
export type AtsFiltersFieldType = (typeof AtsFiltersFieldType)[keyof typeof AtsFiltersFieldType]

/** `ExtensionCriteriaAtsFilterConditionType`. */
export const AtsFilterConditionType = {
  IncludeAny: 'INCLUDE_ANY',
  IncludeAll: 'INCLUDE_ALL',
  Exclude: 'EXCLUDE',
} as const
export type AtsFilterConditionType = (typeof AtsFilterConditionType)[keyof typeof AtsFilterConditionType]

/** `IAtsFiltersFieldConfigurationDTO`. */
export interface AtsFiltersFieldConfiguration {
  /** `ExtensionCriteriaAtsFiltersSection` — the VALUE the Field select stores. */
  section: string
  type: AtsFiltersFieldType
  /** The enum-ish name. ⚠️ The select ORDERS BY THIS and DISPLAYS `displayName`. */
  name: string
  displayName: string
}

export interface AtsFiltersWhitelabelConfiguration {
  entityType?: string
  fields: AtsFiltersFieldConfiguration[]
}

/** `IExtensionCriteriaAtsFilterValueData` — one option of a single/multi select. */
export interface AtsFilterValue {
  id: string
  text: string
}

export type AtsFiltersSingleselectValues = Record<string, AtsFilterValue[]>

/**
 * `getConfigurationsByWhitelabel`.
 *
 * `entityType` is omitted entirely when absent — upstream builds the request config without a
 * `params` key at all rather than sending `entityType=null`.
 */
export async function getAtsFiltersConfiguration(entityType?: string): Promise<AtsFiltersWhitelabelConfiguration> {
  const path = '/chrome-extension-api/ats-filters/getConfigurationsByWhitelabel'
  const url = entityType === undefined ? path : `${path}?entityType=${encodeURIComponent(entityType)}`
  return request('GET', url)
}

/** `getAtsFiltersSingleselectValuesBySections` — the options for every SINGLE_SELECT in use. */
export async function getAtsFiltersSingleselectValues(
  sections: readonly string[],
  entityType?: string,
): Promise<AtsFiltersSingleselectValues> {
  const params = new URLSearchParams()
  // Repeated, not joined — see the note at the top.
  for (const section of sections) params.append('sections', section)
  if (entityType !== undefined) params.append('entityType', entityType)
  return request(
    'GET',
    `/chrome-extension-api/ats-filters/getAtsFiltersSingleselectValuesBySections?${params.toString()}`,
  )
}

/**
 * `getAtsFiltersMultiselectAutocompleteValues` — type-ahead over one section's values.
 *
 * ⚠️ IT IS ALSO THE EXPORT PANEL'S "existing list" LOOKUP, which is not what the name says.
 * `typeaheads.service.ng1.ts:206` defines `getShortlistAutocomplete` as exactly this call with
 * `section: 'SHORTLISTS'`, and `getClientContactAutocomplete` as the same with the contact
 * entity type — so the ATS's hotlists/tearsheets reach the export panel through the
 * criteria-builder's filter endpoint rather than through anything export-shaped.
 *
 * `entityType` is omitted when absent rather than sent as `null`: upstream passes `null` and
 * Angular's `$http` drops null params from the query string, which is the same wire.
 */
export async function getAtsFiltersMultiselectAutocomplete(params_: {
  searchQuery: string
  section: string
  entityType?: string
}): Promise<AtsFilterValue[]> {
  const params = new URLSearchParams({ searchQuery: params_.searchQuery, section: params_.section })
  if (params_.entityType !== undefined) params.append('entityType', params_.entityType)
  return request('GET', `/chrome-extension-api/ats-filters/getAtsFiltersMultiselectAutocompleteValues?${params.toString()}`)
}

/** `getShortlistAutocomplete` — the candidate lists an export can target. */
export async function getShortlistAutocomplete(searchQuery: string): Promise<AtsFilterValue[]> {
  return getAtsFiltersMultiselectAutocomplete({ searchQuery, section: 'SHORTLISTS', entityType: 'CANDIDATE' })
}

/** `getClientContactAutocomplete` — the same for contact lists. */
export async function getClientContactAutocomplete(searchQuery: string): Promise<AtsFilterValue[]> {
  return getAtsFiltersMultiselectAutocomplete({ searchQuery, section: 'SHORTLISTS', entityType: 'CLIENT_CONTACT' })
}
